import { action } from "typesafe-actions";
import { IBusinessPolicyInterested } from "./businessPolicyInterested.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { buildBinderRequest } from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-business-tabs (Interested Parties)
// Optional lead scope for the Interested Parties binder sub-section. When supplied, the
// thunks below fetch/save keyed by `lead_id` (no `customer_policy_id`) and re-fetch by
// `lead_id` after a successful save, routed through the centralized endpoint map. When
// absent, the original customer-policy path is used byte-for-byte.
export interface ILeadInterestedScope {
  leadId: string;
}

const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

let leadInterestedSaveInFlight = false;

export const FETCH_BUSINESS_POLICY_INTERESTED_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_INTERESTED_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_INTERESTED_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_INTERESTED_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_INTERESTED_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_INTERESTED_LIST_FAILED";

export const fetchBusinessPolicyIntrestedListProgress = () =>
  action(FETCH_BUSINESS_POLICY_INTERESTED_LIST_PROGRESS);
export const fetchBusinessPolicyIntrestedListSuccess = (
  data: IBusinessPolicyInterested[]
) => action(FETCH_BUSINESS_POLICY_INTERESTED_LIST_SUCCESS, { data });
export const fetchBusinessPolicyIntrestedListFailed = () =>
  action(FETCH_BUSINESS_POLICY_INTERESTED_LIST_FAILED);

export const fetchBusinessPolicyIntrestedListAsync =
  (customerPolicyId: number, status?: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyIntrestedListProgress());
      let finalUrl = `/policy/get-policy-business-interested-parties?customer_policy_id=${customerPolicyId}`;

      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyInterested[] = res.data.data;
      dispatch(fetchBusinessPolicyIntrestedListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyIntrestedListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Lead-scoped Interested Parties list fetch. Keyed by `lead_id` via the centralized
// endpoint map (never `customer_policy_id`).
export const fetchLeadBusinessInterestedListAsync =
  (
    scope: ILeadInterestedScope,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyIntrestedListProgress());
      const request = buildBinderRequest(
        "interested-party",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      let finalUrl = request.url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyInterested[] = res.data.data;
      dispatch(fetchBusinessPolicyIntrestedListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyIntrestedListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Interested Parties binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinesPolicyInterestedAsync =
  (
    interested: IBusinessPolicyInterested,
    onCallback: (isSuccess: boolean,data?: IBusinessPolicyInterested) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(`/policy/edit-policy-business-interested-parties`, {
        ...interested,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Intrested Parties saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Lead-scoped Interested Parties save. Persists with `lead_id` and NO
// `customer_policy_id` (stripped from the record). On success it re-fetches the list by
// the same `lead_id`.
export const addLeadBusinessInterestedAsync =
  (
    interested: IBusinessPolicyInterested,
    scope: ILeadInterestedScope,
    onCallback: (isSuccess: boolean, data?: IBusinessPolicyInterested) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (leadInterestedSaveInFlight) {
      return;
    }
    leadInterestedSaveInFlight = true;
    try {
      dispatch(saveLoaderProgress());
      const request = buildBinderRequest(
        "interested-party",
        { kind: "lead", leadId: scope.leadId },
        "save"
      );
      const {
        customer_id,
        customer_policy_id,
        ...rest
      } = interested as IBusinessPolicyInterested & {
        customer_id?: unknown;
        customer_policy_id?: unknown;
      };
      const res = await api.post(
        request.url,
        { ...rest, ...request.body },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Intrested Parties saved successfully!",
          displayAs: "snackbar",
        })
      );
      dispatch(fetchLeadBusinessInterestedListAsync(scope, status));
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Interested Parties could not be saved. Please try again.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      leadInterestedSaveInFlight = false;
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_BUSINESS_POLICY_INTERESTED_PROGRESS =
  "FETCH_BUSINESS_POLICY_INTERESTED_PROGRESS";
export const FETCH_BUSINESS_POLICY_INTERESTED_SUCCESS =
  "FETCH_BUSINESS_POLICY_INTERESTED_SUCCESS";
export const FETCH_BUSINESS_POLICY_INTERESTED_FAILED =
  "FETCH_BUSINESS_POLICY_INTERESTED_FAILED";

export const fetchBusinessPolicyIntrestedProgress = () =>
  action(FETCH_BUSINESS_POLICY_INTERESTED_PROGRESS);
export const fetchBusinessPolicyIntrestedSuccess = (
  data: IBusinessPolicyInterested
) => action(FETCH_BUSINESS_POLICY_INTERESTED_SUCCESS, { data });
export const fetchBusinessPolicyIntrestedFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_INTERESTED_FAILED, { errorMessage });

export const fetchBusinessPolicyIntrestedAsync =
  (
    customerPolicyId: number,
    interestedNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyIntrestedProgress());
      const res = await api.get(
        `/policy/get-policy-business-interested-parties?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${interestedNumber}`
      );
      const data: IBusinessPolicyInterested[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyIntrestedSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyIntrestedFailed(
            "Oops! We couldn't find any records associated with your intrested parties number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyIntrestedFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Lead-scoped single Interested Party fetch (by incremental number). Keyed by `lead_id`
// (never `customer_policy_id`) via the centralized endpoint map.
export const fetchLeadBusinessInterestedAsync =
  (
    scope: ILeadInterestedScope,
    interestedNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyIntrestedProgress());
      const request = buildBinderRequest(
        "interested-party",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const finalUrl = `${request.url}&column=incremental_number&value=${interestedNumber}`;
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyInterested[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyIntrestedSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyIntrestedFailed(
            "Oops! We couldn't find any records associated with your intrested parties number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyIntrestedFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Interested Parties binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_BUSINES_POLICY_INTERESTED_STATE =
  "CLEAR_BUSINES_POLICY_INTERESTED_STATE";
export const CLEAR_BUSINES_POLICY_INTERESTED =
  "CLEAR_BUSINES_POLICY_INTERESTED";

export const clearBusinessPolicyIntrestedState = () =>
  action(CLEAR_BUSINES_POLICY_INTERESTED_STATE);

export const clearBusinessPolicyIntrested = () =>
  action(CLEAR_BUSINES_POLICY_INTERESTED);
